import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraColorPicker } from '../../../../components/AtiraColorPicker';
import { AtiraSelect } from '../../../../components/AtiraSelect';
import { AtiraSlider } from '../../../../components/AtiraSlider';
import { Flex } from '../../../../components/Flex';
import { Section } from '../../../../components/Section';
import { Text } from '../../../../components/Text';
import { atiraLinkTestIDs } from '../../../../constants/test/atiralink.testIds';
import { CreateAtiraLinkDto } from '../../../../model/atira-link/dto/CreateAtiraLinkDto';
import { EditAtiraLinkDto } from '../../../../model/atira-link/dto/EditAtiraLinkDto';
import { Breakpoints } from '../../../../theme/Breakpoints';
import { FontSize } from '../../../../theme/FontSize';
import { Spacing } from '../../../../theme/Spacing';
import {
  borderRadiusOptions,
  borderStyleOptions,
} from '../../utils/styleOptions';

const StyledSection = styled(Section)`
  .content-wrapper {
    gap: ${Spacing.l};
  }
`;

const Description = styled(Text)`
  color: ${({ theme }) => theme.fgMuted};
  font-size: ${FontSize.s};
`;

const ResponsiveFlex = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};

  @media (min-width: ${Breakpoints.TABLET}) {
    flex-direction: row;
  }
`;

const InputWrapper = styled(Flex)`
  & > div {
    width: 100%;
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    width: 50%;
  }
`;

export const AtiraLinkStyleImage: React.FC = () => {
  const { t } = useTranslation();

  const { control, watch } = useFormContext<
    CreateAtiraLinkDto | EditAtiraLinkDto
  >();

  const hasBorderWidth = Number(watch('styles.image.borderWidth')) > 0;

  return (
    <StyledSection title={t('atiraLink.create.design.profileImage.title')}>
      <Flex flexDirection="column" gap={'s'}>
        <Description>
          {t('atiraLink.create.design.profileImage.description')}
        </Description>

        <Description>
          <Trans
            i18nKey={'atiraLink.create.design.borderWidth.enable.note'}
            components={[<strong />]}
          />
        </Description>
      </Flex>

      <ResponsiveFlex>
        <Controller
          control={control}
          name="styles.image.borderWidth"
          rules={{
            required: false,
            max: {
              value: 10,
              message: t('atiraLink.create.design.borderWidth.error'),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <InputWrapper>
              <AtiraSlider
                title={t('common.borderWidth')}
                errorMessage={error?.message}
                valid={!error}
                onChange={(e) => onChange(e.toString())}
                dataTestId={atiraLinkTestIDs.design.image.borderWidth}
                min={0}
                max={10}
                value={Number(value || 0)}
              />
            </InputWrapper>
          )}
        />

        <Controller
          control={control}
          name="styles.image.borderStyle"
          rules={{ required: false }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <InputWrapper>
              <AtiraSelect
                title={t('common.borderStyle')}
                onChange={onChange}
                value={value}
                errorMessage={error?.message}
                valid={!error}
                options={borderStyleOptions(
                  t,
                  atiraLinkTestIDs.design.image.borderStyleOption,
                )}
                disabled={!hasBorderWidth}
                onClear={() => onChange(null)}
                dataTestId={atiraLinkTestIDs.design.image.borderStyle}
              />
            </InputWrapper>
          )}
        />
      </ResponsiveFlex>

      <ResponsiveFlex>
        <Controller
          control={control}
          name="styles.image.borderRadius"
          rules={{ required: false }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <InputWrapper>
              <AtiraSelect
                title={t('common.borderRadius')}
                onChange={onChange}
                value={value}
                options={borderRadiusOptions(
                  t,
                  atiraLinkTestIDs.design.image.borderRadiusOption,
                )}
                errorMessage={error?.message}
                valid={!error}
                onClear={() => onChange(null)}
                dataTestId={atiraLinkTestIDs.design.image.borderRadius}
              />
            </InputWrapper>
          )}
        />

        <Controller
          control={control}
          name="styles.image.borderColor"
          rules={{ required: false }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <InputWrapper>
              <AtiraColorPicker
                title={t('common.borderColor')}
                onChange={(v) => onChange(v?.toHexString())}
                value={value}
                errorMessage={error?.message}
                valid={!error}
                allowClear
                onClear={() => onChange(null)}
                dataTestId={atiraLinkTestIDs.design.image.borderColor}
                disabled={!hasBorderWidth}
              />
            </InputWrapper>
          )}
        />
      </ResponsiveFlex>
    </StyledSection>
  );
};
